import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Paper,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Feature } from "models/api";
import { useAlert } from "contexts/alertContext";
import EmptyContent from "components/EmptyContent";
import ActionLoader from "components/UI/ActionLoader";
import {
  getWorkshopsPerClient,
  getVendorCompanies,
  IWorkshopsPerClientReport,
  ICompanyOption,
} from "framework/reports";
import { handleError } from "helpers/utils";
import { getUserData } from "helpers";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { FileDownload, PictureAsPdf } from "@mui/icons-material";

interface Props {
  feature: Feature;
}

const WorkshopsPerClient: React.FC<Props> = ({ feature }) => {
  const { t } = useTranslation();
  const { setAlert } = useAlert();
  const [loading, setLoading] = useState<boolean>(false);
  const [companies, setCompanies] = useState<ICompanyOption[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<string>("");
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [reportData, setReportData] = useState<IWorkshopsPerClientReport[]>([]);
  const userData = getUserData();
  const { colors } = userData;

  const fetchCompanies = useCallback(async () => {
    try {
      const data = await getVendorCompanies();
      setCompanies(data);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error),
        type: "error",
      });
    }
  }, [setAlert]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const handleGenerateReport = async () => {
    setLoading(true);
    try {
      const data = await getWorkshopsPerClient(
        fromDate || undefined,
        toDate || undefined,
        selectedCompany || undefined
      );
      setReportData(data);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error),
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyChange = (event: SelectChangeEvent<string>) => {
    setSelectedCompany(event.target.value);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(
      reportData.map((row) => ({
        [t("T_company")]: row.company,
        [t("T_topic")]: row.topic,
        [t("T_from_date")]: formatDate(row.fromDate),
        [t("T_to_date")]: formatDate(row.toDate),
        [t("T_trainer")]: row.trainer,
        [t("T_pretest_average")]: row.pretestAverage.toFixed(2),
        [t("T_posttest_average")]: row.posttestAverage.toFixed(2),
        [t("T_evaluation_average")]: row.evaluationAverage.toFixed(2),
        [t("T_enrollment_count")]: row.enrollmentCount,
      }))
    );

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, t("T_workshops_per_client"));
    XLSX.writeFile(workbook, `WorkshopsPerClient_${new Date().getTime()}.xlsx`);
  };

  const exportToPDF = () => {
    const doc = new jsPDF({
      orientation: "landscape",
    });

    doc.setFontSize(18);
    doc.text(t("T_workshops_per_client"), 14, 22);
    doc.setFontSize(11);

    const tableData = reportData.map((row) => [
      row.company,
      row.topic,
      formatDate(row.fromDate),
      formatDate(row.toDate),
      row.trainer,
      row.pretestAverage.toFixed(2),
      row.posttestAverage.toFixed(2),
      row.evaluationAverage.toFixed(2),
      row.enrollmentCount?.toString() || "0",
    ]);

    autoTable(doc, {
      head: [
        [
          t("T_company"),
          t("T_topic"),
          t("T_from_date"),
          t("T_to_date"),
          t("T_trainer"),
          t("T_pretest_average"),
          t("T_posttest_average"),
          t("T_evaluation_average"),
          t("T_enrollment_count"),
        ],
      ],
      body: tableData,
      startY: 30,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [41, 128, 185] },
    });

    doc.save(`WorkshopsPerClient_${new Date().getTime()}.pdf`);
  };

  return (
    <Stack spacing={3} p={2}>
      <Typography variant="h4" fontWeight={600}>
        {t("T_workshops_per_client")}
      </Typography>

      <Card>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>{t("T_select_company")}</InputLabel>
                <Select
                  value={selectedCompany}
                  onChange={handleCompanyChange}
                  label={t("T_select_company")}
                >
                  <MenuItem value="">
                    <em>{t("T_all")}</em>
                  </MenuItem>
                  {companies.map((company) => (
                    <MenuItem key={company.id} value={company.id}>
                      {company.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="date"
                label={t("T_from_date")}
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="date"
                label={t("T_to_date")}
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleGenerateReport}
                disabled={loading}
                sx={{
                  height: "56px",
                  bgcolor: colors?.menuItemSelectedColor || "primary.main",
                  "&:hover": {
                    bgcolor: colors?.menuItemSelectedColor || "primary.dark",
                  },
                }}
              >
                {t("T_generate_report")}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading && <ActionLoader position="relative" />}

      {!loading && reportData.length > 0 && (
        <>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              startIcon={<FileDownload />}
              onClick={exportToExcel}
              sx={{
                color: colors?.menuItemSelectedColor || "primary.main",
                borderColor: colors?.menuItemSelectedColor || "primary.main",
              }}
            >
              {t("T_export_excel")}
            </Button>
            <Button
              variant="outlined"
              startIcon={<PictureAsPdf />}
              onClick={exportToPDF}
              sx={{
                color: colors?.menuItemSelectedColor || "primary.main",
                borderColor: colors?.menuItemSelectedColor || "primary.main",
              }}
            >
              {t("T_export_pdf")}
            </Button>
          </Stack>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: colors?.menuItemSelectedColor || "primary.main",
                  }}
                >
                  <TableCell sx={{ color: "white", fontWeight: 600 }}>
                    {t("T_company")}
                  </TableCell>
                  <TableCell sx={{ color: "white", fontWeight: 600 }}>
                    {t("T_topic")}
                  </TableCell>
                  <TableCell sx={{ color: "white", fontWeight: 600 }}>
                    {t("T_from_date")}
                  </TableCell>
                  <TableCell sx={{ color: "white", fontWeight: 600 }}>
                    {t("T_to_date")}
                  </TableCell>
                  <TableCell sx={{ color: "white", fontWeight: 600 }}>
                    {t("T_trainer")}
                  </TableCell>
                  <TableCell sx={{ color: "white", fontWeight: 600 }}>
                    {t("T_pretest_average")}
                  </TableCell>
                  <TableCell sx={{ color: "white", fontWeight: 600 }}>
                    {t("T_posttest_average")}
                  </TableCell>
                  <TableCell sx={{ color: "white", fontWeight: 600 }}>
                    {t("T_evaluation_average")}
                  </TableCell>
                  <TableCell sx={{ color: "white", fontWeight: 600 }}>
                    {t("T_enrollment_count")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.map((row, index) => (
                  <TableRow key={index} hover>
                    <TableCell>{row.company}</TableCell>
                    <TableCell>{row.topic}</TableCell>
                    <TableCell>{formatDate(row.fromDate)}</TableCell>
                    <TableCell>{formatDate(row.toDate)}</TableCell>
                    <TableCell>{row.trainer}</TableCell>
                    <TableCell>{row.pretestAverage.toFixed(2)}</TableCell>
                    <TableCell>{row.posttestAverage.toFixed(2)}</TableCell>
                    <TableCell>{row.evaluationAverage.toFixed(2)}</TableCell>
                    <TableCell>{row.enrollmentCount}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {!loading && reportData.length === 0 && (
        <EmptyContent message={t("T_no_data_available")} />
      )}
    </Stack>
  );
};

export default WorkshopsPerClient;

